(function () {
  const TYPE_MAP = {
    "Suit Jacket": "suit-jacket",
    Blazer: "blazer",
    "Tux Jacket": "tux-jacket",
    Trouser: "trouser",
    Uniform: "uniform",
    Shirt: "shirt",
  };

  const state = {
    data: null,
    mode: "page",
    profile: null,
    gender: null, // "men" | "women"
    heightKey: null,
    step: 0,
    measureIndex: 0,
    inputs: {},
    skipped: new Set(),
    savedProfile: null,
  };

  function isWomenUniformProfile(profileKey) {
    return profileKey === "uniform-women";
  }

  function resolveUniformProfile(gender) {
    return gender === "women" ? "uniform-women" : "uniform";
  }

  function $(sel, root = document) {
    return root.querySelector(sel);
  }

  function el(tag, className, html) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function choiceCard({ kicker, title, selected, onSelect }) {
    const btn = el("button", "vellor-sg__card");
    btn.type = "button";
    btn.setAttribute("aria-pressed", selected ? "true" : "false");
    if (selected) btn.classList.add("is-selected");

    const kickerEl = el("div", "vellor-sg__card-kicker");
    kickerEl.textContent = kicker;
    const titleEl = el("div", "vellor-sg__card-title");
    titleEl.textContent = title;
    btn.append(kickerEl, titleEl);

    btn.addEventListener("click", () => onSelect(btn));
    return btn;
  }

  function setChoiceSelection(container, selectedBtn) {
    container.querySelectorAll(".vellor-sg__card").forEach((card) => {
      const active = card === selectedBtn;
      card.classList.toggle("is-selected", active);
      card.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function buildHeightSelect(data, { id, selectedKey = "regular", labelText = "Height profile" } = {}) {
    const field = el("div", "vellor-sg__field");
    const label = document.createElement("label");
    label.htmlFor = id;
    label.textContent = labelText;
    const select = document.createElement("select");
    select.id = id;
    data.heightSections.forEach((section) => {
      const option = document.createElement("option");
      option.value = section.heightKey;
      option.textContent = section.heightLabel;
      option.selected = section.heightKey === selectedKey;
      select.appendChild(option);
    });
    field.append(label, select);
    return field;
  }

  function heightLabel(data, heightKey) {
    return data.heightSections.find((section) => section.heightKey === heightKey)?.heightLabel || "";
  }

  function profileFromProductType(type) {
    return TYPE_MAP[type] || "suit-jacket";
  }

  function profileMeasurements(data, profileKey) {
    const profile = data.profiles[profileKey];
    if (!profile) return [];
    return data.measurements.filter((m) => {
      if (!m.field) return profileKey === "trouser" && m.key === "outseam";
      return profile.categoryKeys.includes(m.field);
    });
  }

  function heightSection(data, heightKey) {
    return data.heightSections.find((s) => s.heightKey === heightKey) || data.heightSections[1];
  }

  function usesJacketCode(profileKey) {
    return ["suit-jacket", "blazer", "tux-jacket", "uniform"].includes(profileKey);
  }

  function isTrouserProfile(profileKey) {
    return profileKey === "trouser";
  }

  function formatTrouserSize(row) {
    if (!row) return "";
    const waist = row.trouser_waist ?? row.shop_size;
    const inseam = row.inseam;
    if (waist == null) return String(row.shop_size || "");
    if (inseam == null) return String(waist);
    return `${waist}x${inseam}`;
  }

  function parseTrouserSizeInput(raw) {
    const value = String(raw || "").trim();
    if (!value) return {};
    const match = value.match(/^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)$/i);
    if (!match) return {};
    return { trouser_waist: match[1], inseam: match[2] };
  }

  function roundUpMeasurement(value) {
    return Math.ceil(Number(value));
  }

  function sortedSectionRows(section, data) {
    return section.rows
      .filter((row) => data.shopSizes.includes(row.shop_size))
      .sort((a, b) => Number(a.body_chest ?? a.trouser_waist ?? a.shop_size) - Number(b.body_chest ?? b.trouser_waist ?? b.shop_size));
  }

  /** Pick the smallest catalog row at or above the measurement (never size down). */
  function findRowRoundUp(section, data, field, value) {
    if (value == null || value === "") return null;
    const target = roundUpMeasurement(value);
    const rows = sortedSectionRows(section, data).filter((row) => row[field] != null);
    if (!rows.length) return null;
    return rows.find((row) => Number(row[field]) >= target) || rows[rows.length - 1];
  }

  function bumpRowForChest(section, data, row, chest) {
    if (!row || chest == null || chest === "") return row;
    const target = roundUpMeasurement(chest);
    if (Number(row.body_chest) >= target) return row;
    const rows = sortedSectionRows(section, data).filter((item) => item.body_chest != null);
    const index = rows.findIndex((item) => item.shop_size === row.shop_size);
    for (let i = Math.max(index + 1, 0); i < rows.length; i += 1) {
      if (Number(rows[i].body_chest) >= target) return rows[i];
    }
    return rows[rows.length - 1];
  }

  function findWomenPantsSuitRow(data) {
    const chart = data.womenPantsSuit;
    if (!chart?.rows?.length) return null;
    const bust = state.inputs.bust ?? state.inputs.body_chest;
    const waist = state.inputs.waist ?? state.inputs.trouser_waist;
    const hip = state.inputs.hip ?? state.inputs.hips;
    const rows = [...chart.rows].sort((a, b) => Number(a.bust) - Number(b.bust));

    let best = null;
    let bestScore = Infinity;
    for (const row of rows) {
      let score = 0;
      let count = 0;
      const pairs = [
        ["bust", bust],
        ["waist", waist],
        ["hip", hip],
      ];
      for (const [field, input] of pairs) {
        if (input == null || input === "" || row[field] == null) continue;
        const rounded = roundUpMeasurement(input);
        const actual = Number(row[field]);
        if (actual < rounded) score += 1000 + (rounded - actual);
        else score += actual - Number(input);
        count += 1;
      }
      if (!count) continue;
      const normalized = score / count;
      if (normalized < bestScore) {
        bestScore = normalized;
        best = row;
      }
    }
    if (best) return best;
    if (bust != null && bust !== "") {
      const target = roundUpMeasurement(bust);
      return rows.find((row) => Number(row.bust) >= target) || rows[rows.length - 1];
    }
    return null;
  }

  function findBestRow(data) {
    if (isWomenUniformProfile(state.profile)) {
      return findWomenPantsSuitRow(data);
    }

    const section = heightSection(data, state.heightKey || "regular");
    const profile = data.profiles[state.profile];
    if (!section || !profile) return null;

    const profileKey = state.profile || "suit-jacket";
    const trouserSizing = isTrouserProfile(profileKey);
    const chest = trouserSizing ? null : state.inputs.body_chest;
    const eligibleRows = sortedSectionRows(section, data).filter((row) => {
      if (trouserSizing) {
        const waist = state.inputs.trouser_waist;
        const inseam = state.inputs.inseam;
        if (waist != null && waist !== "" && row.trouser_waist != null) {
          if (Number(row.trouser_waist) < roundUpMeasurement(waist)) return false;
        }
        if (inseam != null && inseam !== "" && row.inseam != null) {
          if (Number(row.inseam) < roundUpMeasurement(inseam)) return false;
        }
        return true;
      }
      if (chest == null || chest === "" || row.body_chest == null) return true;
      return Number(row.body_chest) >= roundUpMeasurement(chest);
    });

    let best = null;
    let bestScore = Infinity;

    for (const row of eligibleRows) {
      let score = 0;
      let count = 0;
      for (const field of profile.categoryKeys) {
        const input = state.inputs[field];
        const actual = row[field];
        if (input == null || input === "" || actual == null) continue;
        const rounded = roundUpMeasurement(input);
        if (Number(actual) < rounded) {
          score += 1000 + (rounded - Number(actual));
        } else {
          score += Number(actual) - Number(input);
        }
        count += 1;
      }
      if (!count) continue;
      const normalized = score / count;
      if (normalized < bestScore) {
        bestScore = normalized;
        best = row;
      }
    }

    if (best) {
      return trouserSizing ? best : bumpRowForChest(section, data, best, chest);
    }

    if (trouserSizing) {
      const waist = state.inputs.trouser_waist;
      const inseam = state.inputs.inseam;
      const rows = sortedSectionRows(section, data);
      if (waist && inseam) {
        const targetWaist = roundUpMeasurement(waist);
        const targetInseam = roundUpMeasurement(inseam);
        return (
          rows.find(
            (row) =>
              row.trouser_waist != null &&
              row.inseam != null &&
              Number(row.trouser_waist) >= targetWaist &&
              Number(row.inseam) >= targetInseam,
          ) || rows[rows.length - 1]
        );
      }
      if (waist) return findRowRoundUp(section, data, "trouser_waist", waist);
      if (inseam) return findRowRoundUp(section, data, "inseam", inseam);
      return null;
    }

    if (chest) return findRowRoundUp(section, data, "body_chest", chest);

    return null;
  }

  function buildRecommendation(data) {
    const row = findBestRow(data);
    if (!row) return null;

    const profileKey = state.profile || "suit-jacket";
    const heightKey = state.heightKey || "regular";
    if (isWomenUniformProfile(profileKey)) {
      return {
        row,
        profileKey,
        heightKey: null,
        heightLabel: row.general || "Women's",
        shopSize: row.shop_size,
        jacketCode: null,
        displaySize: row.shop_size,
      };
    }
    const displaySize = usesJacketCode(profileKey)
      ? row.jacket_size || row.shop_size
      : isTrouserProfile(profileKey)
        ? formatTrouserSize(row)
        : row.shop_size;

    return {
      row,
      profileKey,
      heightKey,
      heightLabel: heightLabel(data, heightKey),
      shopSize: row.shop_size,
      jacketCode: row.jacket_size || null,
      displaySize,
    };
  }

  function recommendSize(data) {
    return buildRecommendation(data)?.displaySize || null;
  }

  function renderProgress(container, step, total = 4) {
    const wrap = el("div", "vellor-sg__progress");
    for (let i = 0; i < total; i += 1) {
      const bar = el("div", "vellor-sg__progress-step");
      if (i < step) bar.classList.add("is-done");
      if (i === step) bar.classList.add("is-active");
      wrap.appendChild(bar);
    }
    container.appendChild(wrap);
  }

  function renderGenderStep(root, data) {
    const panel = el("div", "vellor-sg__panel");
    panel.innerHTML =
      '<h2 class="vellor-sg__panel-title">Men or Women?</h2><p class="vellor-sg__panel-sub">Uniforms use different size charts. Pick the fit you need.</p>';
    const grid = el("div", "vellor-sg__grid");
    [
      { key: "men", title: "Men" },
      { key: "women", title: "Women" },
    ].forEach((opt) => {
      const btn = choiceCard({
        kicker: "Fit",
        title: opt.title,
        selected: state.gender === opt.key,
        onSelect: () => {
          state.gender = opt.key;
          state.profile = resolveUniformProfile(opt.key);
          state.measureIndex = 0;
          state.inputs = {};
          state.step = isWomenUniformProfile(state.profile) ? 2 : 1;
          renderApp(root);
        },
      });
      grid.appendChild(btn);
    });
    panel.appendChild(grid);
    const back = el("button", "vellor-sg__trigger-btn vellor-sg__trigger-btn--ghost");
    back.type = "button";
    back.textContent = "Back";
    back.style.marginTop = "1rem";
    back.addEventListener("click", () => {
      state.profile = null;
      state.gender = null;
      state.step = 0;
      renderApp(root);
    });
    panel.appendChild(back);
    root.appendChild(panel);
  }

  function renderCategoryStep(root, data) {
    const panel = el("div", "vellor-sg__panel");
    panel.innerHTML =
      '<h2 class="vellor-sg__panel-title">What are you sizing for?</h2><p class="vellor-sg__panel-sub">Choose a category. We\'ll only ask for measurements that matter for that piece.</p>';
    const grid = el("div", "vellor-sg__grid");
    Object.entries(data.profiles).forEach(([key, profile]) => {
      // Hide gendered uniform-women from top-level; chosen via Men/Women step
      if (key === "uniform-women") return;
      const btn = choiceCard({
        kicker: "Category",
        title: key === "uniform" ? "Uniform" : profile.label,
        selected: state.profile === key || (key === "uniform" && isWomenUniformProfile(state.profile)),
        onSelect: () => {
          if (key === "uniform") {
            state.profile = "uniform";
            state.gender = null;
            state.measureIndex = 0;
            state.step = "gender";
            renderApp(root);
            return;
          }
          state.profile = key;
          state.gender = profile.gender || null;
          state.measureIndex = 0;
          state.step = 1;
          renderApp(root);
        },
      });
      btn.dataset.profile = key;
      grid.appendChild(btn);
    });
    panel.appendChild(grid);
    root.appendChild(panel);
  }

  function renderWomenMeasurementsStep(root, data) {
    const panel = el("div", "vellor-sg__panel");
    panel.innerHTML =
      '<h2 class="vellor-sg__panel-title">Women\'s measurements</h2><p class="vellor-sg__panel-sub">Enter bust, waist, and hip in inches. We\'ll recommend a US size from the Women Pants Suit chart.</p>';
    const fieldRow = el("div", "vellor-sg__field-row");
    [
      ["bust", "Bust (in)", "e.g. 36"],
      ["waist", "Waist (in)", "e.g. 29"],
      ["hip", "Hip (in)", "e.g. 39"],
    ].forEach(([key, label, placeholder]) => {
      const field = el("div", "vellor-sg__field");
      field.innerHTML = `<label for="vv-${key}">${label}</label><input id="vv-${key}" type="number" step="0.1" min="20" max="70" placeholder="${placeholder}">`;
      const input = field.querySelector("input");
      if (state.inputs[key] != null) input.value = state.inputs[key];
      fieldRow.appendChild(field);
    });
    panel.appendChild(fieldRow);

    const actions = el("div", "vellor-sg__actions");
    actions.style.marginTop = "1rem";
    const back = el("button", "vellor-sg__trigger-btn vellor-sg__trigger-btn--ghost");
    back.type = "button";
    back.textContent = "Back";
    back.addEventListener("click", () => {
      state.step = "gender";
      renderApp(root);
    });
    const next = el("button", "vellor-sg__trigger-btn");
    next.type = "button";
    next.textContent = "See my size";
    next.addEventListener("click", () => {
      ["bust", "waist", "hip"].forEach((key) => {
        const input = $(`#vv-${key}`, panel);
        if (input?.value) state.inputs[key] = input.value;
        else delete state.inputs[key];
      });
      state.step = 3;
      renderApp(root);
    });
    actions.append(back, next);
    panel.appendChild(actions);

    if (data.womenPantsSuit?.notes?.length) {
      const notes = el("p", "vellor-sg__panel-sub");
      notes.style.marginTop = "1rem";
      notes.textContent = data.womenPantsSuit.notes.join(" ");
      panel.appendChild(notes);
    }
    root.appendChild(panel);
  }

  function renderHeightStep(root, data) {
    if (isWomenUniformProfile(state.profile)) {
      renderWomenMeasurementsStep(root, data);
      return;
    }
    const trouserSizing = isTrouserProfile(state.profile);
    const panel = el("div", "vellor-sg__panel");
    panel.innerHTML = trouserSizing
      ? '<h2 class="vellor-sg__panel-title">Height profile</h2><p class="vellor-sg__panel-sub">Height determines your inseam length. Enter your trouser size as waist × inseam (e.g. 30×32) — skip anything you\'re unsure about.</p>'
      : '<h2 class="vellor-sg__panel-title">Height profile</h2><p class="vellor-sg__panel-sub">Chest + height determine your jacket length code (S / R / L). Enter what you know — skip anything you\'re unsure about.</p>';
    const grid = el("div", "vellor-sg__grid");
    grid.setAttribute("role", "group");
    grid.setAttribute("aria-label", "Height profile");
    data.heightSections.forEach((section) => {
      const btn = choiceCard({
        kicker: "Height",
        title: section.heightLabel,
        selected: state.heightKey === section.heightKey,
        onSelect: (card) => {
          state.heightKey = section.heightKey;
          setChoiceSelection(grid, card);
        },
      });
      btn.dataset.height = section.heightKey;
      grid.appendChild(btn);
    });
    panel.appendChild(grid);

    const fieldRow = el("div", "vellor-sg__field-row");
    fieldRow.style.marginTop = "1rem";
    let sizeInput = null;

    if (trouserSizing) {
      const sizeField = el("div", "vellor-sg__field");
      sizeField.innerHTML =
        '<label for="vv-trouser-size">Trouser size</label><input id="vv-trouser-size" type="text" inputmode="numeric" placeholder="e.g. 32x32" autocomplete="off">';
      fieldRow.appendChild(sizeField);
      sizeInput = sizeField.querySelector("input");
      if (state.inputs.trouser_waist != null && state.inputs.inseam != null) {
        sizeInput.value = `${state.inputs.trouser_waist}x${state.inputs.inseam}`;
      }
    } else {
      const chestField = el("div", "vellor-sg__field");
      chestField.innerHTML =
        '<label for="vv-chest">Chest (in)</label><input id="vv-chest" type="number" step="0.1" min="30" max="60" placeholder="e.g. 40">';
      fieldRow.appendChild(chestField);
      sizeInput = chestField.querySelector("input");
      if (state.inputs.body_chest != null) sizeInput.value = state.inputs.body_chest;
    }

    panel.appendChild(fieldRow);

    function collectSizingInputs() {
      if (trouserSizing) {
        const combined = parseTrouserSizeInput(sizeInput.value);
        if (combined.trouser_waist) state.inputs.trouser_waist = combined.trouser_waist;
        if (combined.inseam) state.inputs.inseam = combined.inseam;
        delete state.inputs.body_chest;
        return;
      }
      const chest = sizeInput.value.trim();
      if (chest) state.inputs.body_chest = chest;
    }

    const actions = el("div", "vellor-sg__actions");
    const back = el("button", "vellor-sg__btn vellor-sg__btn--ghost", "Back");
    back.type = "button";
    back.addEventListener("click", () => {
      state.step = 0;
      renderApp(root);
    });
    const next = el("button", "vellor-sg__btn", "Continue");
    next.type = "button";
    next.addEventListener("click", () => {
      collectSizingInputs();
      if (!state.heightKey) state.heightKey = "regular";
      state.measureIndex = 0;
      state.step = 2;
      renderApp(root);
    });
    const skip = el("button", "vellor-sg__btn vellor-sg__btn--link", "Skip for now");
    skip.type = "button";
    skip.addEventListener("click", () => {
      state.heightKey = state.heightKey || "regular";
      state.step = 2;
      renderApp(root);
    });
    actions.append(back, skip, next);
    panel.appendChild(actions);
    root.appendChild(panel);
  }

  const PENDING_FIT_KEY = "vellor_vault_pending_fit";
  const FIT_STORAGE_PREFIX = "vellor_vault_fit_profile";

  function parseSavedProfile(raw) {
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function storageKey(cfg = window.VellorSizeGuide || {}) {
    const id = cfg.customer?.id;
    return id ? `${FIT_STORAGE_PREFIX}_${id}` : FIT_STORAGE_PREFIX;
  }

  function loadStoredProfile(cfg = window.VellorSizeGuide || {}) {
    try {
      return parseSavedProfile(localStorage.getItem(storageKey(cfg)));
    } catch {
      return null;
    }
  }

  function persistStoredProfile(cfg, profile) {
    if (!profile) return;
    try {
      localStorage.setItem(storageKey(cfg), JSON.stringify(profile));
    } catch {
      /* ignore quota errors */
    }
    window.VellorSizeGuide.savedProfile = profile;
    state.savedProfile = profile;
  }

  function resolveSavedProfile(cfg = window.VellorSizeGuide || {}) {
    return parseSavedProfile(cfg.savedProfile) || loadStoredProfile(cfg) || state.savedProfile || null;
  }

  function applyProfileToState(profile) {
    if (!profile) return;
    if (profile.profile) state.profile = profile.profile;
    if (profile.heightKey) state.heightKey = profile.heightKey;
    if (profile.measurements) state.inputs = { ...profile.measurements };
  }

  async function customerApiRequest(cfg, query, variables = {}) {
    const candidates = [cfg.customerApiUrl, "/customer/api/2025-01/graphql"].filter(Boolean);
    let lastError = null;
    for (const url of [...new Set(candidates)]) {
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ query, variables }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || body.errors?.length) {
          const message = body.errors?.map((item) => item.message).join("; ") || "Customer API request failed.";
          throw new Error(message);
        }
        return body.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error("Customer API is not configured.");
  }

  async function fetchCustomerFitProfile(cfg = window.VellorSizeGuide || {}) {
    if (!cfg.customer?.loggedIn) return null;

    if (cfg.loadUrl) {
      try {
        const res = await fetch(cfg.loadUrl, { credentials: "same-origin" });
        const body = await res.json().catch(() => ({}));
        if (res.ok && body.ok && body.profile) return body.profile;
      } catch {
        /* fall through */
      }
    }

    try {
      const data = await customerApiRequest(
        cfg,
        `query VellorSavedFit {
          customer {
            metafield(namespace: "custom", key: "fit_profile") {
              value
            }
          }
        }`,
      );
      return parseSavedProfile(data?.customer?.metafield?.value);
    } catch {
      return null;
    }
  }

  async function saveFitProfileViaCustomerApi(cfg, payload) {
    const data = await customerApiRequest(
      cfg,
      `mutation VellorSaveFit($metafields: [MetafieldsSetInput!]!) {
        metafieldsSet(metafields: $metafields) {
          metafields { namespace key }
          userErrors { field message code }
        }
      }`,
      {
        metafields: [
          {
            namespace: "custom",
            key: "fit_profile",
            type: "json",
            value: JSON.stringify(payload),
          },
        ],
      },
    );
    const errors = data?.metafieldsSet?.userErrors || [];
    if (errors.length) {
      throw new Error(errors.map((item) => item.message).join("; "));
    }
    return data;
  }

  function formatSavedFitLabel(profile) {
    if (!profile?.recommendedSize) return "";
    const parts = [profile.recommendedSize];
    if (profile.heightLabel) parts.push(profile.heightLabel);
    if (profile.recommendedShopSize && profile.recommendedShopSize !== profile.recommendedSize) {
      parts.push(`select size ${profile.recommendedShopSize}`);
    }
    return parts.join(" · ");
  }

  function renderSavedFitBanner(root, data) {
    const host = $("#vellor-saved-fit-banner");
    const profile = resolveSavedProfile();
    const target = host || root;
    if (!target) return;

    const existing = target.querySelector(".vellor-sg__saved-banner");
    if (existing) existing.remove();
    if (!profile?.recommendedSize) return;

    const banner = el("div", "vellor-sg__saved-banner");
    banner.innerHTML = `<p><strong>Your saved fit:</strong> ${formatSavedFitLabel(profile)}</p>`;
    const actions = el("div", "vellor-sg__actions");
    const viewBtn = el("button", "vellor-sg__btn", "View recommendation");
    viewBtn.type = "button";
    viewBtn.addEventListener("click", () => {
      applyProfileToState(profile);
      state.step = 3;
      renderApp(root);
    });
    const updateBtn = el("button", "vellor-sg__btn vellor-sg__btn--ghost", "Update measurements");
    updateBtn.type = "button";
    updateBtn.addEventListener("click", () => {
      applyProfileToState(profile);
      state.step = profile.profile ? 1 : 0;
      renderApp(root);
    });
    actions.append(viewBtn, updateBtn);
    banner.appendChild(actions);
    target.prepend(banner);
  }

  function refreshSavedFitUi(root = null) {
    const profile = resolveSavedProfile();
    state.savedProfile = profile;
    document.querySelectorAll("[data-vvellor-saved-fit]").forEach((node) => {
      if (profile?.recommendedSize) {
        node.hidden = false;
        node.innerHTML = `<strong>Your saved fit:</strong> ${formatSavedFitLabel(profile)}`;
      } else {
        node.hidden = true;
        node.textContent = "";
      }
    });
    const appRoot = root || $("#vellor-size-guide-app");
    if (appRoot && state.data) renderSavedFitBanner(appRoot, state.data);
  }

  async function hydrateSavedProfile() {
    const cfg = window.VellorSizeGuide || {};
    let profile = resolveSavedProfile(cfg);

    if (cfg.customer?.loggedIn) {
      const remote = await fetchCustomerFitProfile(cfg);
      if (remote) {
        profile = remote;
        persistStoredProfile(cfg, remote);
      }
    }

    if (profile) {
      state.savedProfile = profile;
      refreshSavedFitUi();
    }
  }

  function fieldKeyForMeasure(measure) {
    return measure.field || measure.key;
  }

  function buildFitPayload(data) {
    const rec = buildRecommendation(data);
    const heightKey = state.heightKey || "regular";
    return {
      profile: state.profile,
      heightKey,
      heightLabel: rec?.heightLabel || heightLabel(data, heightKey),
      recommendedSize: rec?.displaySize || null,
      recommendedShopSize: rec?.shopSize || null,
      recommendedJacketCode: rec?.jacketCode || null,
      measurements: { ...state.inputs },
      savedFrom: window.location.pathname,
    };
  }

  function storePendingFit(data) {
    try {
      sessionStorage.setItem(PENDING_FIT_KEY, JSON.stringify(buildFitPayload(data)));
    } catch {
      /* ignore storage errors */
    }
  }

  async function saveFitProfile(data) {
    const cfg = window.VellorSizeGuide || {};
    const payload = {
      ...buildFitPayload(data),
      savedAt: new Date().toISOString(),
    };
    persistStoredProfile(cfg, payload);
    refreshSavedFitUi();

    let accountSaved = false;
    let accountError = null;

    if (cfg.customer?.loggedIn && cfg.customerApiUrl) {
      try {
        await saveFitProfileViaCustomerApi(cfg, payload);
        accountSaved = true;
      } catch (error) {
        accountError = error;
      }
    }

    if (!accountSaved && cfg.saveUrl) {
      try {
        const res = await fetch(cfg.saveUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok && body.ok) accountSaved = true;
        else if (!accountError) accountError = new Error(body.error || "Account save failed.");
      } catch (error) {
        if (!accountError) accountError = error;
      }
    }

    if (accountSaved) return { accountSaved: true, payload };
    if (cfg.customer?.loggedIn) {
      throw new Error(
        accountError?.message ||
          "Saved on this device. We couldn't sync to your account yet — please try again shortly.",
      );
    }
    return { accountSaved: false, payload };
  }

  function buildLoginUrl() {
    const cfg = window.VellorSizeGuide || {};
    const joiner = window.location.search ? "&" : "?";
    const returnUrl = `${window.location.pathname}${window.location.search}${joiner}restore_fit=1`;
    return `${cfg.customer?.loginUrl || "/account/login"}?return_url=${encodeURIComponent(returnUrl)}`;
  }

  function collectMeasurementInputs(root, measures) {
    measures.forEach((measure) => {
      const key = fieldKeyForMeasure(measure);
      const input = root.querySelector(`#vv-${key}`);
      const val = input?.value?.trim();
      if (val) state.inputs[key] = val;
    });
  }

  function renderMeasurementsStep(root, data) {
    const measures = profileMeasurements(data, state.profile);
    const profileLabel = data.profiles[state.profile]?.label || "Garment";
    const panel = el("div", "vellor-sg__panel");

    panel.innerHTML = `<h2 class="vellor-sg__panel-title">${profileLabel} measurements</h2><p class="vellor-sg__panel-sub">Enter everything you know on this screen. Tap a measurement or select a field — the diagram updates as you go.</p>`;

    const pills = el("div", "vellor-sg__measure-pills");
    const wrap = el("div", "vellor-sg__measure");
    const fig = el("figure", "vellor-sg__figure");
    const figCaption = el("p", "vellor-sg__figure-caption");
    fig.appendChild(figCaption);

    const fields = el("div", "vellor-sg__measure-fields");

    let activeKey = fieldKeyForMeasure(measures.find((m) => m.imageUrl) || measures[0]);

    function setActiveMeasure(measure) {
      if (!measure) return;
      activeKey = fieldKeyForMeasure(measure);
      pills.querySelectorAll(".vellor-sg__pill").forEach((pill) => {
        pill.classList.toggle("is-active", pill.dataset.measureKey === activeKey);
      });
      fields.querySelectorAll(".vellor-sg__measure-field").forEach((field) => {
        field.classList.toggle("is-active", field.dataset.measureKey === activeKey);
      });
      if (measure.imageUrl) {
        fig.innerHTML = `<img src="${measure.imageUrl}" alt="${measure.label} measurement diagram">`;
        fig.appendChild(figCaption);
      } else {
        fig.innerHTML = "";
        fig.appendChild(figCaption);
      }
      figCaption.textContent = measure.help || "";
    }

    measures.forEach((measure) => {
      const key = fieldKeyForMeasure(measure);

      const pill = el("button", "vellor-sg__pill");
      pill.type = "button";
      pill.dataset.measureKey = key;
      pill.textContent = measure.label;
      if (state.inputs[key]) pill.classList.add("is-filled");
      pill.addEventListener("click", () => {
        setActiveMeasure(measure);
        pill.classList.add("is-active");
        const input = fields.querySelector(`#vv-${key}`);
        if (input) input.focus();
      });
      pills.appendChild(pill);

      const item = el("div", "vellor-sg__measure-field");
      item.dataset.measureKey = key;
      item.innerHTML = `<label for="vv-${key}">${measure.label} (${measure.unit})</label><input id="vv-${key}" type="number" step="0.1" placeholder="Optional">`;
      const input = item.querySelector("input");
      if (state.inputs[key] != null) input.value = state.inputs[key];
      input.addEventListener("focus", () => setActiveMeasure(measure));
      input.addEventListener("input", () => {
        pill.classList.toggle("is-filled", Boolean(input.value.trim()));
      });
      fields.appendChild(item);
    });

    setActiveMeasure(measures.find((m) => fieldKeyForMeasure(m) === activeKey) || measures[0]);

    wrap.append(fig, fields);
    panel.append(pills, wrap);

    const actions = el("div", "vellor-sg__actions");
    const back = el("button", "vellor-sg__btn vellor-sg__btn--ghost", "Back");
    back.type = "button";
    back.addEventListener("click", () => {
      state.step = 1;
      renderApp(root);
    });

    const next = el("button", "vellor-sg__btn", "See my size");
    next.type = "button";
    next.addEventListener("click", () => {
      collectMeasurementInputs(panel, measures);
      state.step = 3;
      renderApp(root);
    });

    actions.append(back, next);
    panel.appendChild(actions);
    root.appendChild(panel);
  }

  function renderSaveSection(panel, data) {
    const cfg = window.VellorSizeGuide || {};
    const saveWrap = el("div", "vellor-sg__save");
    const status = el("p", "vellor-sg__save-status");

    if (cfg.customer?.loggedIn) {
      const copy = el(
        "p",
        "vellor-sg__panel-sub",
        "Save these measurements to your Vellor Vault account so you don't have to re-enter them next time.",
      );
      saveWrap.appendChild(copy);
      const saveBtn = el("button", "vellor-sg__btn", "Save to my account");
      saveBtn.type = "button";
      saveBtn.addEventListener("click", async () => {
        saveBtn.disabled = true;
        status.textContent = "Saving your fit profile…";
        status.className = "vellor-sg__save-status";
        try {
          const result = await saveFitProfile(data);
          status.textContent = result.accountSaved
            ? "Saved to your account. We'll remember this fit next time you visit."
            : "Saved on this device.";
          status.classList.add("is-success");
          sessionStorage.removeItem(PENDING_FIT_KEY);
        } catch (error) {
          status.textContent = error.message;
          status.classList.add("is-error");
          saveBtn.disabled = false;
        }
      });
      saveWrap.append(saveBtn, status);
    } else {
      const copy = el(
        "p",
        "vellor-sg__panel-sub",
        "Log in to save these measurements to your customer profile.",
      );
      saveWrap.appendChild(copy);
      const loginBtn = el("a", "vellor-sg__btn", "Log in to save");
      loginBtn.href = buildLoginUrl();
      loginBtn.addEventListener("click", () => storePendingFit(data));
      saveWrap.append(loginBtn, status);
    }

    panel.appendChild(saveWrap);
  }

  async function tryRestorePendingFit(data, root) {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("restore_fit")) return;
    const cfg = window.VellorSizeGuide || {};
    if (!cfg.customer?.loggedIn) return;

    const raw = sessionStorage.getItem(PENDING_FIT_KEY);
    if (!raw) return;

    try {
      const pending = JSON.parse(raw);
      state.profile = pending.profile || state.profile;
      state.heightKey = pending.heightKey || state.heightKey;
      state.inputs = pending.measurements || {};
      state.step = 3;
      renderApp(root);
      await saveFitProfile(data);
      const status = $(".vellor-sg__save-status", root);
      if (status) {
        status.textContent = "Saved to your account. We'll remember this fit next time you visit.";
        status.classList.add("is-success");
      }
      refreshSavedFitUi(root);
    } catch {
      /* keep wizard state if auto-save fails */
    }
  }

  function renderResultStep(root, data) {
    const rec = buildRecommendation(data);
    const size = rec?.displaySize || null;
    const panel = el("div", "vellor-sg__panel vellor-sg__result");
    const profileLabel = data.profiles[state.profile]?.label || "Garment";
    panel.innerHTML = `<div class="vellor-sg__eyebrow">Your Vault fit</div><h2 class="vellor-sg__panel-title">${profileLabel} recommendation</h2>`;

    if (size) {
      const heightText = rec.heightLabel;
      const shopNote =
        rec.shopSize && usesJacketCode(state.profile)
          ? ` Select ready-to-ship chest size <strong>${rec.shopSize}</strong> on the product page.`
          : "";
      panel.innerHTML += `<div class="vellor-sg__result-size">${size}</div><p class="vellor-sg__panel-sub">Based on your measurements${heightText ? ` and height profile <strong>${heightText}</strong>` : ""}.${shopNote}</p>`;
    } else {
      const hint = isTrouserProfile(state.profile)
        ? "Enter your trouser size (e.g. 32×32), or open the full chart to compare across sizes."
        : "Add chest and at least one more measurement, or open the full chart to compare across sizes.";
      panel.innerHTML += `<p class="vellor-sg__panel-sub">${hint}</p>`;
    }

    const actions = el("div", "vellor-sg__actions");
    actions.style.justifyContent = "center";

    const chartBtn = el("button", "vellor-sg__btn", "View full size chart");
    chartBtn.type = "button";
    chartBtn.addEventListener("click", () =>
      openModal({
        profile: state.profile,
        heightKey: state.heightKey || "regular",
        highlightSize: size,
      }),
    );

    const restart = el("button", "vellor-sg__btn vellor-sg__btn--ghost", "Start over");
    restart.type = "button";
    restart.addEventListener("click", () => {
      state.step = 0;
      state.measureIndex = 0;
      state.inputs = {};
      state.skipped = new Set();
      renderApp(root);
    });

    actions.append(chartBtn, restart);
    panel.appendChild(actions);
    renderSaveSection(panel, data);
    persistStoredProfile(window.VellorSizeGuide || {}, {
      ...buildFitPayload(data),
      savedAt: new Date().toISOString(),
    });
    refreshSavedFitUi(root);
    root.appendChild(panel);
  }

  function collectQuickSizingInputs(root, profileKey) {
    state.profile = profileKey;
    state.heightKey = $("#vv-quick-height", root)?.value || "regular";
    if (isTrouserProfile(profileKey)) {
      const combined = parseTrouserSizeInput($("#vv-quick-trouser-size", root)?.value || "");
      state.inputs.trouser_waist = combined.trouser_waist || "";
      state.inputs.inseam = combined.inseam || "";
      delete state.inputs.body_chest;
      return;
    }
    state.inputs.body_chest = $("#vv-quick-chest", root)?.value || "";
  }

  function renderQuick(root, data, profileKey) {
    const trouserSizing = isTrouserProfile(profileKey);
    root.innerHTML = trouserSizing
      ? '<h3 class="vellor-sg__panel-title">Find your size</h3><p class="vellor-sg__panel-sub">Enter trouser size (waist × inseam) and height for a quick recommendation, or view the full chart.</p>'
      : '<h3 class="vellor-sg__panel-title">Find your size</h3><p class="vellor-sg__panel-sub">Enter chest and height for a quick recommendation, or view the full chart.</p>';

    const quick = el("div", "vellor-sg__quick");
    if (trouserSizing) {
      const size = el("div", "vellor-sg__field");
      size.innerHTML =
        '<label for="vv-quick-trouser-size">Trouser size</label><input type="text" inputmode="numeric" id="vv-quick-trouser-size" placeholder="32x32">';
      quick.appendChild(size);
    } else {
      const chest = el("div", "vellor-sg__field");
      chest.innerHTML =
        '<label for="vv-quick-chest">Chest (in)</label><input type="number" step="0.1" id="vv-quick-chest" placeholder="40">';
      quick.appendChild(chest);
    }
    const height = buildHeightSelect(data, {
      id: "vv-quick-height",
      selectedKey: state.heightKey || "regular",
    });
    quick.appendChild(height);
    root.appendChild(quick);

    const out = el("div", "vellor-sg__result");
    out.style.display = "none";
    root.appendChild(out);

    const actions = el("div", "vellor-sg__actions");
    const find = el("button", "vellor-sg__btn", "Find my size");
    find.type = "button";
    find.addEventListener("click", () => {
      collectQuickSizingInputs(root, profileKey);
      const rec = buildRecommendation(data);
      const size = rec?.displaySize || null;
      out.style.display = "block";
      const missingHint = trouserSizing
        ? "Enter your trouser size (e.g. 32×32) for a recommendation."
        : "Enter chest measurement for a recommendation.";
      out.innerHTML = size
        ? `<div class="vellor-sg__result-size">${size}</div><p class="vellor-sg__panel-sub">Recommended fit for height profile <strong>${rec.heightLabel}</strong>${rec.shopSize && usesJacketCode(profileKey) ? ` · select chest size <strong>${rec.shopSize}</strong> when ordering` : ""}.</p>`
        : `<p class="vellor-sg__panel-sub">${missingHint}</p>`;
    });
    const chart = el("button", "vellor-sg__btn vellor-sg__btn--ghost", "View full size chart");
    chart.type = "button";
    chart.addEventListener("click", () => {
      collectQuickSizingInputs(root, profileKey);
      openModal({
        profile: profileKey,
        heightKey: state.heightKey,
        highlightSize: recommendSize(data),
      });
    });
    actions.append(find, chart);
    root.appendChild(actions);
  }

  function renderApp(root) {
    root.innerHTML = "";
    if (state.mode === "quick") {
      renderQuick(root, state.data, state.profile);
      return;
    }

    const progressStep =
      state.step === "gender" ? 1 : typeof state.step === "number" ? state.step : 0;
    renderProgress(root, progressStep);

    if (state.step === 0) renderCategoryStep(root, state.data);
    else if (state.step === "gender") renderGenderStep(root, state.data);
    else if (state.step === 1) renderHeightStep(root, state.data);
    else if (state.step === 2) {
      if (isWomenUniformProfile(state.profile)) renderWomenMeasurementsStep(root, state.data);
      else renderMeasurementsStep(root, state.data);
    } else renderResultStep(root, state.data);
  }

  function rowValue(row, field) {
    if (row[field] != null && row[field] !== "") return row[field];
    if (field === "leg_opening" && row.straight_leg_opening != null) return row.straight_leg_opening;
    return null;
  }

  function profileCodeLabel(profileKey) {
    if (profileKey === "trouser") return "Waist size";
    if (profileKey === "shirt") return "Shirt code";
    return "Jacket code";
  }

  function showProfileCode(profileKey) {
    return profileKey !== "trouser";
  }

  function rowMatchesHighlight(row, highlight, profileKey) {
    if (!highlight) return false;
    if (usesJacketCode(profileKey)) {
      return row.jacket_size === highlight || row.shop_size === highlight;
    }
    if (isTrouserProfile(profileKey)) {
      return (
        formatTrouserSize(row) === highlight ||
        row.shop_size === highlight ||
        String(row.trouser_waist) === String(highlight)
      );
    }
    return row.shop_size === highlight || String(row.trouser_waist) === String(highlight);
  }

  function rowDisplaySize(row, profileKey) {
    return row.shop_size;
  }

  function sizeColumnLabel(profileKey) {
    return isTrouserProfile(profileKey) ? "Suit size" : "Size";
  }

  function buildWomenPantsSuitTable(data, highlightSize) {
    const chart = data.womenPantsSuit;
    if (!chart?.rows?.length) return "<p>Women size chart unavailable.</p>";
    const fields = ["bust", "waist", "hip", "shoulder", "coat_length", "sleeve", "pants_length"];
    let html = '<div class="vellor-sg-table-wrap"><table class="vellor-sg-table"><thead><tr>';
    html += "<th>General</th><th>US Size</th>";
    fields.forEach((f) => {
      html += `<th>${data.fieldLabels[f] || f}</th>`;
    });
    html += "</tr></thead><tbody>";
    chart.rows.forEach((row) => {
      const cls = String(row.shop_size) === String(highlightSize) ? ' class="is-match"' : "";
      html += `<tr${cls}><td>${row.general || ""}</td><td>${row.shop_size}</td>`;
      fields.forEach((f) => {
        html += `<td>${row[f] ?? "—"}</td>`;
      });
      html += "</tr>";
    });
    html += "</tbody></table></div>";
    if (chart.notes?.length) {
      html += `<p class="vellor-sg__panel-sub">${chart.notes.join(" ")}</p>`;
    }
    return html;
  }

  function buildTable(data, profileKey, heightKey, highlightSize) {
    if (isWomenUniformProfile(profileKey)) {
      return buildWomenPantsSuitTable(data, highlightSize);
    }
    const section = heightSection(data, heightKey || "regular");
    const profile = data.profiles[profileKey] || data.profiles["suit-jacket"];
    const fields = profile.categoryKeys;

    let html = '<div class="vellor-sg-table-wrap"><table class="vellor-sg-table"><thead><tr>';
    html += `<th>${sizeColumnLabel(profileKey)}</th>`;
    if (showProfileCode(profileKey)) {
      html += `<th>${profileCodeLabel(profileKey)}</th>`;
    }
    fields.forEach((f) => {
      html += `<th>${data.fieldLabels[f] || f}</th>`;
    });
    html += "</tr></thead><tbody>";

    section.rows.forEach((row) => {
      if (!data.shopSizes.includes(row.shop_size)) return;
      const cls = rowMatchesHighlight(row, highlightSize, profileKey) ? ' class="is-match"' : "";
      html += `<tr${cls}><td>${rowDisplaySize(row, profileKey)}</td>`;
      if (showProfileCode(profileKey)) {
        html += `<td>${row.jacket_size ?? "—"}</td>`;
      }
      fields.forEach((f) => {
        const val = rowValue(row, f);
        html += `<td>${val ?? "—"}</td>`;
      });
      html += "</tr>";
    });

    html += "</tbody></table></div>";
    return html;
  }

  function ensureModal() {
    let modal = $("#vellor-sg-modal");
    if (modal) return modal;

    modal = el("div", "vellor-sg-modal");
    modal.id = "vellor-sg-modal";
    modal.innerHTML = `
      <div class="vellor-sg-modal__backdrop" data-close="1"></div>
      <div class="vellor-sg-modal__dialog color-scheme color-scheme--scheme-1" role="dialog" aria-modal="true" aria-labelledby="vellor-sg-modal-title">
        <div class="vellor-sg-modal__header">
          <div>
            <div class="vellor-sg__eyebrow">Vellor Vault</div>
            <h2 class="vellor-sg__panel-title" id="vellor-sg-modal-title">Size chart</h2>
            <p class="vellor-sg__panel-sub" id="vellor-sg-modal-sub"></p>
          </div>
          <button type="button" class="vellor-sg-modal__close" aria-label="Close">&times;</button>
        </div>
        <div class="vellor-sg__field-row" id="vellor-sg-modal-controls"></div>
        <div id="vellor-sg-modal-body"></div>
      </div>`;
    document.body.appendChild(modal);

    modal.querySelector("[data-close]").addEventListener("click", closeModal);
    modal.querySelector(".vellor-sg-modal__close").addEventListener("click", closeModal);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeModal();
    });
    return modal;
  }

  function openModal(opts = {}) {
    const run = () => {
      const modal = ensureModal();
      let profileKey = opts.profile || state.profile || "suit-jacket";
      if (opts.gender === "women" && (profileKey === "uniform" || profileKey === "blazer")) {
        profileKey = "uniform-women";
      }
      const heightKey = opts.heightKey || state.heightKey || "regular";
      const highlightSize = opts.highlightSize || null;

      state.profile = profileKey;
      const profile = state.data.profiles[profileKey] || state.data.profiles["suit-jacket"];
      $("#vellor-sg-modal-title").textContent = `${profile.label} size chart`;

      const controls = $("#vellor-sg-modal-controls");
      controls.innerHTML = "";
      const body = $("#vellor-sg-modal-body");

      if (isWomenUniformProfile(profileKey)) {
        $("#vellor-sg-modal-sub").textContent = "Women Pants Suit chart (inches)";
        body.innerHTML = buildWomenPantsSuitTable(state.data, highlightSize);
      } else {
        $("#vellor-sg-modal-sub").textContent = heightSection(state.data, heightKey).heightLabel;
        const heightField = buildHeightSelect(state.data, {
          id: "vellor-sg-modal-height",
          selectedKey: heightKey,
        });
        controls.appendChild(heightField);
        const render = () => {
          const hk = $("#vellor-sg-modal-height").value;
          state.heightKey = hk;
          const section = heightSection(state.data, hk);
          $("#vellor-sg-modal-sub").textContent = section.heightLabel;
          body.innerHTML = buildTable(state.data, profileKey, hk, highlightSize);
        };
        heightField.querySelector("select").addEventListener("change", render);
        render();
      }

      modal.classList.add("is-open");
      document.documentElement.style.overflow = "hidden";
    };

    if (!state.data) {
      loadData()
        .then((data) => {
          state.data = data;
          run();
        })
        .catch(() => {});
      return;
    }

    run();
  }

  function openQuickModal(profileKey) {
    if (!state.data) {
      loadData()
        .then((data) => {
          state.data = data;
          openQuickModal(profileKey);
        })
        .catch(() => {});
      return;
    }

    let modal = $("#vellor-sg-quick-modal");
    if (!modal) {
      modal = el("div", "vellor-sg-modal vellor-sg-modal--quick");
      modal.id = "vellor-sg-quick-modal";
      modal.innerHTML = `
        <div class="vellor-sg-modal__backdrop" data-close="1"></div>
        <div class="vellor-sg-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="vellor-sg-quick-title">
          <div class="vellor-sg-modal__header">
            <div>
              <div class="vellor-sg__eyebrow">Vellor Vault</div>
              <h2 class="vellor-sg__panel-title" id="vellor-sg-quick-title">Find your size</h2>
              <p class="vellor-sg__panel-sub" id="vellor-sg-quick-sub"></p>
            </div>
            <button type="button" class="vellor-sg-modal__close" aria-label="Close">&times;</button>
          </div>
          <div id="vellor-sg-quick-body"></div>
        </div>`;
      document.body.appendChild(modal);
      modal.querySelector("[data-close]").addEventListener("click", closeQuickModal);
      modal.querySelector(".vellor-sg-modal__close").addEventListener("click", closeQuickModal);
    }

    state.profile = profileKey || state.profile || "suit-jacket";
    const profile = state.data.profiles[state.profile];
    $("#vellor-sg-quick-sub").textContent = profile?.label || "Garment";

    const body = $("#vellor-sg-quick-body");
    body.innerHTML = "";
    state.mode = "quick";
    renderQuick(body, state.data, state.profile);
    state.mode = "page";

    modal.classList.add("is-open");
    document.documentElement.style.overflow = "hidden";
  }

  function closeQuickModal() {
    const modal = $("#vellor-sg-quick-modal");
    if (modal) modal.classList.remove("is-open");
    if (!$("#vellor-sg-modal")?.classList.contains("is-open")) {
      document.documentElement.style.overflow = "";
    }
  }

  function closeModal() {
    const modal = $("#vellor-sg-modal");
    if (modal) modal.classList.remove("is-open");
    if (!$("#vellor-sg-quick-modal")?.classList.contains("is-open")) {
      document.documentElement.style.overflow = "";
    }
  }

  function openChartModal(profileKey) {
    openModal({ profile: profileKey, heightKey: "regular" });
  }

  async function loadData() {
    const cfg = window.VellorSizeGuide || {};
    if (cfg.data) return cfg.data;
    const url = cfg.dataUrl;
    if (!url) throw new Error("Missing Vellor size guide data URL");
    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to load size guide data");
    const data = await res.json();
    if (cfg.images) {
      data.measurements.forEach((m) => {
        if (!m.imageUrl && m.image && cfg.images[m.image]) {
          m.imageUrl = cfg.images[m.image];
        }
      });
    }
    return data;
  }

  function initPage() {
    const root = $("#vellor-size-guide-app");
    if (!root) return;
    state.mode = root.dataset.mode || "page";
    if (root.dataset.profile) state.profile = root.dataset.profile;
    const params = new URLSearchParams(window.location.search);
    if (params.get("profile")) state.profile = params.get("profile");
    if (params.get("gender")) state.gender = params.get("gender");
    if (state.gender === "women" && (state.profile === "uniform" || state.profile === "blazer")) {
      state.profile = "uniform-women";
    }

    loadData()
      .then(async (data) => {
        state.data = data;
        await hydrateSavedProfile();
        if (state.mode === "page" && state.profile && !data.profiles[state.profile]) {
          state.profile = null;
        }
        if (state.mode === "page" && state.profile) {
          if (isWomenUniformProfile(state.profile)) state.step = 2;
          else if (state.profile === "uniform" && !state.gender) state.step = "gender";
          else state.step = 1;
        }
        renderApp(root);
        renderSavedFitBanner(root, data);
        tryRestorePendingFit(data, root);
      })
      .catch((err) => {
        root.innerHTML = `<p class="vellor-sg__panel-sub">Size guide failed to load. ${err.message}</p>`;
      });
  }

  function openSizeFinder({ profile, gender } = {}) {
    let nextProfile = profile || "suit-jacket";
    const nextGender = gender || null;
    if (nextGender === "women" && (nextProfile === "uniform" || nextProfile === "blazer")) {
      nextProfile = "uniform-women";
    }
    const params = new URLSearchParams({ profile: nextProfile });
    if (nextGender) params.set("gender", nextGender);
    window.location.href = `${window.VellorSizeGuide?.pageUrl || "/pages/size-guide"}?${params.toString()}`;
  }

  function initTriggers() {
    document.querySelectorAll("[data-vvellor-size-guide]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const profile = btn.dataset.vvellorSizeGuide || "suit-jacket";
        const gender = btn.dataset.vvellorGender || null;
        openModal({ profile, gender, heightKey: "regular" });
      });
    });

    document.querySelectorAll("[data-vvellor-size-finder]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        openSizeFinder({
          profile: btn.dataset.vvellorSizeFinder || "suit-jacket",
          gender: btn.dataset.vvellorGender || null,
        });
      });
    });
  }

  window.VellorSizeGuideApp = {
    openModal,
    openChartModal,
    openQuickModal,
    openSizeFinder,
    closeModal,
    closeQuickModal,
    recommendSize,
    buildRecommendation,
    profileFromProductType,
  };

  document.addEventListener("DOMContentLoaded", () => {
    hydrateSavedProfile().finally(() => {
      initPage();
      initTriggers();
    });
  });
})();
